import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Briefcase, User } from "lucide-react";

export default function Register() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get("role") === "employeur" ? "employeur" : "candidat";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState(initialRole);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/auth/register", {
        name,
        email,
        password,
        confirm_password: confirmPassword,
        role,
      });
      setUser(res.data);
      navigate("/dashboard", { replace: true, state: { user: res.data } });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const roleCard = (value, Icon, title, desc, testid, activeCls) => (
    <button
      type="button"
      data-testid={testid}
      onClick={() => setRole(value)}
      className={`flex-1 border rounded-xl p-4 text-left transition-colors duration-200 ${
        role === value
          ? activeCls
          : "border-slate-300 hover:border-brand-turquoise text-slate-700"
      }`}
    >
      <Icon className="w-5 h-5 mb-2" />
      <p className="font-semibold text-sm">{title}</p>
      <p className={`text-xs mt-1 ${role === value ? "text-white/70" : "text-slate-500"}`}>{desc}</p>
    </button>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
        <Link to="/" data-testid="register-logo-link" className="inline-block mb-8" aria-label="NEXA — Accueil">
          <Logo />
        </Link>
        <h2 data-testid="register-heading" className="text-2xl font-bold text-brand-navy mb-2">
          Créer un compte
        </h2>
        <p className="text-slate-500 mb-6">Choisissez votre type de compte.</p>

        <div className="flex gap-3 mb-6">
          {roleCard("candidat", User, "Candidat", "Trouvez un emploi et déposez votre CV", "role-candidat-button", "border-brand-turquoise bg-brand-turquoise text-white")}
          {roleCard("employeur", Briefcase, "Employeur", "Publiez vos offres d'emploi", "role-employeur-button", "border-brand-blue bg-brand-blue text-white")}
        </div>

        {error && (
          <div data-testid="register-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="register-name" className="block text-sm font-medium text-brand-navy mb-1">Nom complet</label>
            <input
              id="register-name"
              data-testid="register-name-input"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent"
              placeholder="Jean Dupont"
            />
          </div>
          <div>
            <label htmlFor="register-email" className="block text-sm font-medium text-brand-navy mb-1">Adresse courriel</label>
            <input
              id="register-email"
              data-testid="register-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent"
              placeholder="vous@exemple.com"
            />
          </div>
          <div>
            <label htmlFor="register-password" className="block text-sm font-medium text-brand-navy mb-1">Mot de passe</label>
            <input
              id="register-password"
              data-testid="register-password-input"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent"
              placeholder="8 caractères minimum"
            />
          </div>
          <div>
            <label htmlFor="register-confirm" className="block text-sm font-medium text-brand-navy mb-1">Confirmer le mot de passe</label>
            <input
              id="register-confirm"
              data-testid="register-confirm-input"
              type="password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent"
              placeholder="Répétez le mot de passe"
            />
          </div>
          <button
            data-testid="register-submit-button"
            type="submit"
            disabled={submitting}
            className="w-full bg-brand-navy text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-blue transition-colors duration-200 disabled:opacity-50"
          >
            {submitting ? "Création…" : "Créer mon compte"}
          </button>
        </form>

        <p className="text-sm text-slate-500 mt-6 text-center">
          Déjà un compte ?{" "}
          <Link to="/login" data-testid="register-login-link" className="font-semibold text-brand-turquoise hover:underline">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
}
